
import { createContext, ReactNode, useContext } from 'react';
import api, { apiV2 } from '../services/api';

type LegacyArrayValue = string | string[] | number[] | Date[];
type OrderStatus = 'Pendente' | 'Executado';
type OrderType = 'Entrada' | 'Saída';

interface OrderTransaction {
    id_transaction?: string;
    product_amount: number | string;
    unity_price: number | string;
    total_value: number | string;
    fk_costs_categories_id_category: string;
    fk_products_id_product?: string;
    fk_employees_id_employee?: string;
    [key: string]: any;
}

interface Order {
    id_order: string;
    discount: number | string;
    expiration_date: LegacyArrayValue;
    payment_date: LegacyArrayValue;
    amount_parcels: number;
    type: OrderType;
    status: OrderStatus[] | string;
    values_parcels: string | number | number[];
    total_order_value: number | string;
    total_order_value_exec: number | string;
    isactive: boolean;
    approved: boolean;
    approved_by: string;
    created_by: string;
    fk_costs_center_id_cost?: string;
    fk_costs_center_id_cost_array?: string[] | string;
    fk_providers_id_provider?: string;
    fk_trips_id_trip?: string;
    fk_trips_id_trip_array?: string[] | string;
    fk_subsidiary_id_subsidiary: string;
    fk_paymenttype_id_paymenttype: string;
    transactions: OrderTransaction[];
    created_at: string;
    isstocked: boolean;
    isapportionment: boolean;
    issalary?: boolean;
    receipt: string;
    qty_parcels_exec: number;
    is_indirect_cost: boolean;
    begin_salary_date?: string | Date;
    end_salary_date?: string | Date;
    salary_month?: string;
    total_order_array?: number[];
    notes: string;
    selling_orders?: string[];
    [key: string]: any;
}

interface ChangeExpirationDateRequest {
    id_array: string[];
    expiration_date_array: any[];
    payment_date_array: any[];
    status_array: string[];
}

interface OrdersRequest {
    type?: string | string[];
    costCenter?: string | string[];
    subsidiary?: string | string[];
    provider?: string | string[];
    trip?: string | string[];
    paymentType?: string | string[];
    boat?: string | string[];
    status?: string | string[];
    startDate: string;
    endDate: string;
    pages: number;
    limit: number;
}

type OrderInput = Omit<Order, 'id_order'>

interface OrdersProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
}

interface ResponsePropsWithData {
    success: boolean;
    message: string;
    data: any;
}

interface OrdersContextData {
    getFilteredOrders: (includeInstallments: boolean, request: OrdersRequest) => Promise<ResponsePropsWithData>;
    getOrdersSalary: (request: OrdersRequest) => Promise<ResponsePropsWithData>;
    getOrderTransactions: (order: string) => Promise<ResponsePropsWithData>;
    getOrderSalaryTransactions: (order: string) => Promise<ResponsePropsWithData>;
    getOrderDetails: (order: string) => Promise<ResponsePropsWithData>;
    getOrderSalaryDetails: (order: string) => Promise<ResponsePropsWithData>;
    createOrder: (order: OrderInput) => Promise<ResponseProps>;
    updateOrder: (order: Order) => Promise<ResponseProps>;
    updateOnDelete: (id: string) => Promise<ResponseProps>;
    deleteStockOrder: (id: number) => Promise<ResponseProps>;
    getResultByDate: (startDate: string, endDate: string) => Promise<ResponsePropsWithData>;
    getResultByCategory: (category: string, startDate: string, endDate: string) => Promise<ResponsePropsWithData>;
    shortcutEditOrder: (order: ChangeExpirationDateRequest) => Promise<ResponseProps>;
    getInputOrdersFromTrip: (tripId: string) => Promise<ResponsePropsWithData>;
    getInputOrdersFromOrder: (orderId: string) => Promise<ResponsePropsWithData>;
}

const OrdersContext = createContext<OrdersContextData>(
    {} as OrdersContextData
);

const numberFields = [
  'amount_parcels',
  'total_order_value',
  'total_order_value_exec',
  'discount',
  'qty_parcels_exec'
];

const booleanFields = [
  'approved',
  'isactive',
  'isstocked',
  'isapportionment',
  'issalary',
  'is_indirect_cost'
];

function normalizeBoolean(value: unknown) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  return Boolean(value);
}

function normalizeNumber(value: unknown) {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const trimmedValue = value.trim();
    const normalizedValue = trimmedValue.includes(',') && !trimmedValue.includes('.')
      ? trimmedValue.replace(',', '.')
      : trimmedValue;
    const parsedValue = Number(normalizedValue);

    return Number.isFinite(parsedValue) ? parsedValue : value;
  }

  return value;
}

function normalizeOrderTransaction(transaction: OrderTransaction) {
  return {
    ...transaction,
    product_amount: normalizeNumber(transaction.product_amount),
    unity_price: normalizeNumber(transaction.unity_price),
    total_value: normalizeNumber(transaction.total_value)
  };
}

function normalizeOrderPayload<T extends Partial<Order>>(order: T): T {
  const payload = { ...order } as Record<string, any>;

  numberFields.forEach((field) => {
    if (field in payload) {
      payload[field] = normalizeNumber(payload[field]);
    }
  });

  booleanFields.forEach((field) => {
    if (field in payload) {
      payload[field] = normalizeBoolean(payload[field]);
    }
  });

  if (Array.isArray(payload.transactions)) {
    payload.transactions = payload.transactions.map(normalizeOrderTransaction);
  }

  if (!Array.isArray(payload.selling_orders)) {
    payload.selling_orders = [];
  }

  if (!('notes' in payload) || payload.notes == null) {
    payload.notes = '';
  }

  return payload as T;
}

function isTechnicalMessage(message: unknown) {
  if (typeof message !== 'string') return true;

  return /^(cannot\s+(get|post|put|patch|delete)|axioserror|error:|network error|internal server error|failed with status code)/i.test(message.trim()) ||
    /prismaclient|unique constraint|duplicate key|foreign key|violates|violat|P20\d{2}/i.test(message) ||
    /<\/?[a-z][\s\S]*>/i.test(message) ||
    /\/api\/v\d/i.test(message);
}

function translateKnownOrderApiMessage(message: unknown) {
  if (typeof message !== 'string') return undefined;

  const normalizedMessage = message.trim();

  if (/^validation failed\.?$/i.test(normalizedMessage)) {
    return 'Não foi possível validar os dados do pedido. Revise as informações e tente novamente.';
  }

  if (/^you do not have permission to perform this action\.?$/i.test(normalizedMessage)) {
    return 'Você não tem permissão para realizar esta ação.';
  }

  if (/^unauthorized\.?$/i.test(normalizedMessage)) {
    return 'Sua sessão expirou. Faça login novamente.';
  }

  if (/^user credentials do not match\.?$/i.test(normalizedMessage)) {
    return 'Credenciais de usuário inválidas.';
  }

  if (/^order not found\.?$/i.test(normalizedMessage)) {
    return 'Pedido não encontrado.';
  }

  if (/^order has already been executed\.?$/i.test(normalizedMessage)) {
    return 'Este pedido já foi executado.';
  }

  if (/^installment orders cannot be paid by payment import\.?$/i.test(normalizedMessage)) {
    return 'Pedidos parcelados não podem ser executados pela importação do relatório de pagamento.';
  }

  if (/^only standard orders can be linked to a finished product order\.?$/i.test(normalizedMessage)) {
    return 'Somente pedidos padrão podem ser vinculados a um pedido de produto acabado.';
  }

  if (/^total amount is required\.?$/i.test(normalizedMessage)) {
    return 'Informe o valor total do pedido.';
  }

  if (/invalid.*salary\/month|salary\/month.*invalid/i.test(normalizedMessage)) {
    return 'Informe um mês de salário válido.';
  }

  return undefined;
}

function extractOrderApiMessage(response: any) {
  const message = response?.data?.message || response?.message;

  if (Array.isArray(message)) {
    return message[0];
  }

  return message;
}

function getOrderErrorMessage(response: any, fallback: string) {
  const apiMessage = extractOrderApiMessage(response);
  const translatedMessage = translateKnownOrderApiMessage(apiMessage);

  return translatedMessage || (isTechnicalMessage(apiMessage) ? fallback : apiMessage);
}

export function OrdersProvider({ children }: OrdersProvidersProps) {
  async function getOrderTransactions(order: string) {
    const response = await api.get('/transactions', {
      params: {
        fk_orders_id_order: order
      }
    });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar transações relacionadas ao pedido.",
      data: []
    };
  }

  async function getOrderSalaryTransactions(order: string) {
    const params = {
      fk_orders_id_order: order
    };
    const response = await api.get('/salarytransactions', { params: { ...api.defaults.params, ...params } });
    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar transações relacionadas ao pedido.",
      data: []
    };
  }

  async function getOrderDetails(order: string) {
    const params = {
      id_order: order
    };
    const response = await api.get('/orderdetails', { params: { ...api.defaults.params, ...params } });
    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar os detalhes relacionados ao pedido.",
      data: []
    };
  }

  async function getOrderSalaryDetails(order: string) {
    const params = {
      id_order: order
    };
    const response = await api.get('/salaryorderdetails', { params: { ...api.defaults.params, ...params } });
    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar os detalhes relacionados ao pedido.",
      data: []
    };
  }

  async function getFilteredOrders(includeInstallments: boolean, request: OrdersRequest) {
    let params = {
      ...request
    };
    const response = await api.get(`/ordersfilt?includeInstallments=${includeInstallments}`, { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar pedidos.",
      data: []
    };
  }

  async function getOrdersSalary(request: OrdersRequest) {
    let params = {
      ...request
    };
    const response = await api.get('/salaryordersfilt', { params: { ...api.defaults.params, ...params } });
    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar pedidos.",
      data: []
    };
  }

  async function updateOnDelete(id: string) {
    const response = await apiV2.post('/deleteorder', { id_order: id });
    if (response.status === 200) {
      return {
        success: true,
        message: "Pedido removido com sucesso!"
      };
    }
    return {
      success: false,
      message: getOrderErrorMessage(response, "Erro ao remover pedido.")
    };
  }

  async function deleteStockOrder(id: number) {
    const response = await api.delete('/deletestocktransactions', {
      data: { id_order: id }
    });

    if (response.status === 200) {
      return {
        success: true,
        message: "Pedido removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover pedido."
    };
  }

  async function createOrder(orderInput: OrderInput) {
    if (!Array.isArray(orderInput?.transactions) || orderInput.transactions.length === 0) {
      return {
        success: false,
        message: "É necessário adicionar ao menos um item ao pedido."
      };
    }

    const response = await apiV2.post('/orders', normalizeOrderPayload(orderInput));

    if (response.status === 200 || response.status === 201) {
      return {
        success: true,
        message: "Pedido adicionado com sucesso!",
        id: response.data?.id
      };
    }
    return {
      success: false,
      message: getOrderErrorMessage(response, "Erro ao adicionar pedido.")
    };

  }

  async function updateOrder(order: Order) {
    const response = await apiV2.put('/orders', normalizeOrderPayload(order));
    if (response.status === 200) {
      return {
        success: true,
        message: "Pedido editado com sucesso!"
      };
    }
    return {
      success: false,
      message: getOrderErrorMessage(response, "Erro ao editar pedido.")
    };
  }

  async function getResultByDate(startDate: string, endDate: string) {
    let params = {
      startDate: "'" + startDate + "'",
      endDate: "'" + endDate + "'"
    };
    const response = await api.get('/resultbydate', { params: { ...api.defaults.params, ...params } });
    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar resultado.",
      data: []
    };
  }

  async function getResultByCategory(category: string, startDate: string, endDate: string) {
    let params = {
      startDate: "'" + startDate + "'",
      endDate: "'" + endDate + "'"
    };
    const response = await api.get('/summary/' + category, { params: { ...api.defaults.params, ...params } });
    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar resultado por categoria.",
      data: []
    };
  }

  async function shortcutEditOrder(order: ChangeExpirationDateRequest) {
    const response = await api.put('/shortcut', order);
    if (response.status === 200) {
      return {
        success: true,
        message: "Pedido editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar pedido."
    };
  }

  async function getInputOrdersFromTrip(tripId: string) {
    const params = {
      fk_trips_id_trip: tripId
    };
    const response = await api.get('/inputordersfromtrip', { params: { ...api.defaults.params, ...params } });
    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar [pedidos de entrada de uma viagem] relacionadas ao pedido.",
      data: []
    };
  }

  async function getInputOrdersFromOrder(orderId: string) {
    const response = await api.get('/inputordersfromorder', {
      params: {
        order_id: orderId
      }
    });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar [pedidos de entrada] relacionadps ao pedido.",
      data: []
    };
  }

  return (
    <OrdersContext.Provider value={{ getFilteredOrders, getOrdersSalary, getOrderDetails, getOrderSalaryDetails, getOrderTransactions, getOrderSalaryTransactions, createOrder, updateOnDelete, updateOrder, getResultByDate, getResultByCategory, deleteStockOrder, shortcutEditOrder, getInputOrdersFromTrip, getInputOrdersFromOrder }}>
      {children}
    </OrdersContext.Provider>
  );
}

export function UseOrders() {
  const context = useContext(OrdersContext);
  return context;
}
